import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface Client {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  createdAt: string;
}

interface ClientsContextType {
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  addClient: (name: string, email?: string, phone?: string) => Promise<string | null>;
  deleteClient: (id: string) => void;
}

const ClientsContext = createContext<ClientsContextType | undefined>(undefined);

export function ClientsProvider({ children }: { children: ReactNode }) {
  const [clients, setClients] = useState<Client[]>([]);

  const addClient = async (name: string, email?: string, phone?: string): Promise<string | null> => {
    if (supabase) {
      // Add to Supabase
      const { data, error } = await supabase
        .from('clients')
        .insert([{ name, email, phone }])
        .select()
        .single();

      if (error) {
        console.error('Error adding client to Supabase:', error);
        return null;
      }

      if (data) {
        const newClient: Client = {
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          createdAt: data.created_at,
        };
        setClients((prev) => [...prev, newClient]);
        return data.id;
      }
    } else {
      // Fallback to local storage
      const newClient: Client = {
        id: `client-${Date.now()}-${Math.random()}`,
        name,
        email,
        phone,
        createdAt: new Date().toISOString(),
      };
      setClients((prev) => [...prev, newClient]);
      return newClient.id;
    }
    return null;
  };

  const deleteClient = (id: string) => {
    setClients((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <ClientsContext.Provider
      value={{
        clients,
        setClients,
        addClient,
        deleteClient,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
}

export function useClients() {
  const context = useContext(ClientsContext);
  if (context === undefined) {
    throw new Error('useClients must be used within a ClientsProvider');
  }
  return context;
}

